import { useEffect, useId, useSyncExternalStore, type RefObject } from 'react';
import { FIND_LAYER, findShortcutTarget, firstFindBox } from '@/lib/find-shortcut';

/**
 * The search boxes that take ⌘F / Ctrl+F, by hook id. One window listener serves them all; only the
 * first box in the open layer answers (`lib/find-shortcut.ts`), so a page with two still has one.
 */
const boxes = new Map<string, HTMLInputElement>();
/** The ids of the boxes that would answer, one per layer: they show the key chip. */
let answering: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();
/** A box hidden or shown (a pane at another width, a list behind an open record) can change which answers. */
let watcher: ResizeObserver | null = null;

function refresh() {
  const firsts = new Set<HTMLInputElement>();
  for (const el of boxes.values()) {
    const first = firstFindBox(boxes.values(), el.closest(FIND_LAYER) ?? null);
    if (first) firsts.add(first);
  }
  answering = new Set([...boxes].filter(([, el]) => firsts.has(el)).map(([id]) => id));
  listeners.forEach((listener) => listener());
}

function onKeyDown(event: KeyboardEvent) {
  const layers = document.querySelectorAll(FIND_LAYER);
  const box = findShortcutTarget(event, boxes.values(), layers[layers.length - 1] ?? null);
  if (!box) return;
  event.preventDefault();
  box.focus();
  box.select();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Registers the input for ⌘F / Ctrl+F while `enabled`. Returns whether this box is the one that
 * answers (in its page or dialog), so it alone shows the key chip.
 */
export function useFindShortcut(ref: RefObject<HTMLInputElement | null>, enabled: boolean): boolean {
  const id = useId();
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    if (!boxes.size) {
      window.addEventListener('keydown', onKeyDown);
      watcher = new ResizeObserver(refresh);
    }
    boxes.set(id, el);
    watcher?.observe(el);
    refresh();
    return () => {
      boxes.delete(id);
      watcher?.unobserve(el);
      if (!boxes.size) {
        window.removeEventListener('keydown', onKeyDown);
        watcher?.disconnect();
        watcher = null;
      }
      refresh();
    };
  }, [enabled, id, ref]);
  return useSyncExternalStore(
    subscribe,
    () => answering.has(id),
    () => false
  );
}
